import React, { useState, useRef, useEffect } from 'react'
import PageHeader from '@/components/pageHeader'
import { useSearchParams } from 'react-router-dom'
import style from './index.module.less'
import DebounceSearch from '@/components/debounceSearch'
import Icon, { PlusOutlined } from '@ant-design/icons'
import { Dropdown, Modal, Spin, Splitter, Upload, message } from 'antd'
import type { MenuProps, UploadProps } from 'antd'
import { useTranslation } from 'react-i18next'
import KnowledgeList from './knowledgeList'
import KnowledgeFile from './knowledgeFile'
import { queryVersion, uploadFileDirectory, uploadFileJudgeRepeat } from '@/api/dictionary'
import AddKnowledge from './addKnowledge'
import useDictMenu from '../hooks/useDictMenu'
import { PageType, KnowledgePageType } from '@/views/dictionary/constants'

interface KnowledgeItem {
    id: string;
    name: string;
    isHome?: boolean;
    downloadAddress: string | null;
    downloadAddressAll: string | null;
    fileInfoId: string;
}
const Knowledge = ({ pageType }) => {
    const [searchParams] = useSearchParams()
    const dictName = searchParams.get('dictName')
    const domainId = searchParams.get('domainId')
    const knowledgeFileRef = useRef<any>()
    const knowledgeListRef = useRef<any>()
    const addKnowledgeRef = useRef<any>()
    const { t } = useTranslation()
    const [searchValue, setSearchValue] = useState<string>('')
    const [currentId, setCurrentId] = useState('-1')//默认选中首页 fileInfoId
    const [uploading, setUploading] = useState(false);
    const [version, setVersions] = useState({})
    const [file, setFile] = useState<KnowledgeItem>({ id: '', name: '', downloadAddress: '', downloadAddressAll: '', fileInfoId: '' });

    useEffect(() => {
        setCurrentId('-1')
    },[domainId])

    const {
        reload
    } = useDictMenu({
        includes: [PageType.View, PageType.Audit],
        knowledgeIncludes: [KnowledgePageType.View, KnowledgePageType.Audit],
    });

    const handleCurrentChange = (id: string, item: KnowledgeItem) => {
        setCurrentId(id);
        setFile(item);
    };

    const fetchVersions = async (dictName) => {
        const res = await queryVersion({
            type: pageType,
            dictName,
            domainId,
        }) as any
        setVersions(res)
    }

    useEffect(() => {
        if (!domainId || !dictName) return
        fetchVersions(dictName)
    }, [domainId, dictName])

    const handleUpload = async (formData) => {
        setUploading(true);
        await uploadFileDirectory(formData, { dictId: version[0]?.dictId, domainId });
        message.success(t('ikg.uploadSuccess'));
        knowledgeListRef.current?.getList()
        setUploading(false);
    };

    const uploadProps = {
        accept: '.xlsx,.xls,.pdf,.csv,.doc,.docx',
        showUploadList: false,
        multiple: false,
        customRequest: async (data) => {
            const file = data.file
            const fileName = file.name;
            const fileExtension = fileName?.split('.')?.pop()?.toLowerCase();
            const allowedExtensions = ['xlsx', 'xls', 'pdf', 'csv', 'doc', 'docx'];
            const isValidType = allowedExtensions.includes(fileExtension);
            if (!isValidType) {
                message.error('只支持.xlsx,.xls,.pdf,.csv,.doc,.docx文件!');
                return false;
            }

            const isLt30M = file.size / 1024 / 1024 < 30;
            if (!isLt30M) {
                message.error(t('ikg.fileLimit'));
                return false;
            }
            const formData = new FormData();
            formData.append('file', file);
            // 判断是否重复
            const canUpload = await uploadFileJudgeRepeat(formData, { dictId: version[0]?.dictId })
            if (canUpload) {
                return handleUpload(formData)
            } else {
                // 有重复的确认会覆盖旧的
                Modal.confirm({
                    title: t('ikg.confirmUpload'),
                    content: t('ikg.confirmContent'),
                    okText: t('ikg.confirm'),
                    cancelText: t('ikg.cancel'),
                    onOk: () => handleUpload(formData)
                });
            }
            return
        }
    };

    const items: MenuProps['items'] = [
        {
            key: 'import',
            label: (
                <Upload {...uploadProps}>
                    <span>{t('ikg.import')}</span>
                </Upload>
            ),
        },
    ];

    const handleEdit = () => {
        addKnowledgeRef.current?.open({
            type: 'base',
            moduleType: 2,
            dictType: 2,
            domainId,
            dictId: version?.[0]?.dictId
        });
    };

    const handleEditSuccess = async (type: 'domain' | 'base', name, domainId) => {
        knowledgeListRef.current?.getList();
        await fetchVersions(name);
        // 触发菜单重新加载
        reload();

        // 更新 URL 参数
        const newSearchParams = new URLSearchParams(window.location.search);
        newSearchParams.set('dictName', name);
        window.history.replaceState({}, '', `${window.location.pathname}?${newSearchParams.toString()}`);
    };
    return (
        <Spin spinning={uploading}>
            <div className={style.knowledge}>
                <PageHeader title={version?.[0]?.dictName || dictName}></PageHeader>
                <div className={style['knowledge-container']} style={{ height: `${window.innerHeight - 190}px` }}>
                    <Splitter >
                        <Splitter.Panel defaultSize="300" min="240" max="70%">
                            <div className={style.left}>
                                <div className={style['left-title']}>
                                    <span>{version?.[0]?.dictName || dictName}</span>
                                    {pageType === 3 && <Dropdown
                                        menu={{
                                            items,
                                        }}
                                        placement="bottom"
                                        trigger={['click']}
                                    >
                                        <Icon component={PlusOutlined as any} className={style.actionIcon} />
                                    </Dropdown>}
                                </div>
                                <DebounceSearch
                                    style={{ marginBottom: 8 }}
                                    placeholder={t('ikg.input', { name: t('ikg.keywordRetrieval') })}
                                    onSearch={(val: any) => {
                                        setSearchValue(val)
                                    }}
                                />
                                <KnowledgeList
                                    version={version}
                                    ref={knowledgeListRef}
                                    domainId={domainId}
                                    searchValue={searchValue}
                                    currentId={currentId}
                                    handleCurrentChange={handleCurrentChange}
                                    pageType={pageType}
                                />
                            </div>
                        </Splitter.Panel>
                        <Splitter.Panel>
                            <div className={style.right}>
                                <KnowledgeFile
                                    ref={knowledgeFileRef}
                                    domainId={domainId}
                                    currentId={currentId}
                                    version={version}
                                    file={file}
                                    onEdit={handleEdit}
                                    pageType={pageType}
                                />
                            </div>
                        </Splitter.Panel>
                    </Splitter>
                </div>
                <AddKnowledge editContent={{ dictName: version?.[0]?.dictName, desc: version?.[0]?.description }} isEdit ref={addKnowledgeRef} onSuccess={handleEditSuccess} />
            </div>
        </Spin>
    )
}

export default Knowledge

export const KnowledgeView = () => <Knowledge pageType={PageType.View} />
export const KnowledgeAudit = () => <Knowledge pageType={PageType.Audit} />
